import { useState } from "react";
import { useParams } from "react-router-dom";
import { Table, Button, Modal, Form, Input, DatePicker, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSprints, useCreateSprint } from "../../queries/sprints";
import { useProject } from "../../queries/projects";
import { SprintProps, SprintStatus } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { ProjectNav } from "../../components/ui";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { SprintStatusTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";

export const SprintListPage = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { data: sprints, isLoading, isError, error } = useSprints(projectId!);
  const { data: project } = useProject(projectId!);
  const createSprint = useCreateSprint(projectId!);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleCreate = async () => {
    const values = await form.validateFields();
    try {
      await createSprint.mutateAsync({
        name: values.name,
        goal: values.goal,
        startDate: values.range[0].toISOString(),
        endDate: values.range[1].toISOString(),
      });
      setIsModalOpen(false);
      form.resetFields();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not create the sprint");
    }
  };

  const active = (sprints || []).find((s: SprintProps) => s.status === "active");

  return (
    <PageShell
      kind="list"
      crumbs={[{ label: "Projects", to: "/projects" }, { label: project?.name || "Project" }]}
      title="Sprints"
      meta={[
        { label: "Current sprint", value: active ? active.name : <span style={{ color: "var(--q-text-faint)" }}>None active</span> },
        {
          label: "Ends",
          value: active
            ? <span className="q-num">{dayjs(active.endDate).format("D MMM")}</span>
            : <span style={{ color: "var(--q-text-faint)" }}>—</span>,
        },
      ]}
      actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>New sprint</Button>}
      tabs={<ProjectNav projectId={projectId!} active="sprints" />}
    >
      <Toolbar count={(sprints || []).length} unit="sprints" />
      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<SprintProps>
          rowKey="_id"
          size="small"
          dataSource={sprints || []}
          pagination={false}
          locale={{
            emptyText: (
              <EmptyState
                title="No sprints planned"
                description="Open a sprint to give the board a cadence and start collecting velocity."
                actions={<Button type="primary" onClick={() => setIsModalOpen(true)}>New sprint</Button>}
              />
            ),
          }}
          columns={[
            { title: "Name", dataIndex: "name", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
            {
              title: "Goal", dataIndex: "goal",
              render: (v?: string) => v
                ? <span style={{ color: "var(--q-text-muted)" }}>{v}</span>
                : <span style={{ color: "var(--q-text-faint)" }}>—</span>,
            },
            { title: "Status", dataIndex: "status", width: 140, render: (s: SprintStatus) => <SprintStatusTag status={s} /> },
            {
              title: "Dates", width: 190, align: "right",
              render: (_, sprint) => (
                <span className="q-num" style={{ color: "var(--q-text-muted)" }}>
                  {dayjs(sprint.startDate).format("D MMM")} – {dayjs(sprint.endDate).format("D MMM YYYY")}
                </span>
              ),
            },
          ]}
        />
      </DisplayComponent>

      <Modal
        title="New sprint"
        open={isModalOpen}
        onOk={handleCreate}
        okText="Create sprint"
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={createSprint.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="goal" label="Goal"><Input.TextArea rows={2} placeholder="One sentence the team can hold in their head" /></Form.Item>
          <Form.Item name="range" label="Dates" rules={[{ required: true }]}>
            <DatePicker.RangePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
